import React, { useState, useEffect, useRef } from "react";
import Image from "../../../../CommonComponents/Image";
import SafeIFrame from "../../../../CommonComponents/SafeIFrame";
import HtmlText from "../../../../CommonComponents/HtmlText";
import YoutubeIframe from "../../../../CommonComponents/YoutubeIframe";
import VimeoIframe from "../../../../CommonComponents/VimeoIframe";
import CookieBlockedMessage from "../../../../CommonComponents/CookieBlockedMessage";
import { shouldShowEmbeddedContent } from "../../../../../utis/cookieConsentUtils";

// Component to handle script-based embeds (ThriveCart, Calendly, etc.)
// Uses iframe isolation so each embed has its own JavaScript context
const ScriptEmbed = ({ htmlContent, height, width, onClick }) => {
    const iframeRef = useRef(null);

    useEffect(() => {
        if (!iframeRef.current || !htmlContent) return;

        const iframe = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Create complete HTML document for iframe
        const fullHTML = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body {
            margin: 0;
            padding: 0;
            overflow: auto;
        }
    </style>
</head>
<body>
    ${htmlContent}
</body>
</html>
        `;

        // Write HTML to iframe
        iframeDoc.open();
        iframeDoc.write(fullHTML);
        iframeDoc.close();

    }, [htmlContent]);

    return (
        <iframe
            ref={iframeRef}
            onClick={onClick}
            style={{
                width: `${width}px`,
                height: `${height}px`,
                border: 'none',
                overflow: 'auto'
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            title="Embed Content"
        />
    );
};

const Element = ({ element, setVideoData, actionCableVideoMontior, dimensions, resizeDimensions, zoom, maskedDimensions, bgColor, elemClick , scrollActive, handleClick, handleAction, editortype
}) => {
  // State to force re-render when cookie consent changes
  const [, setCookieStatusChanged] = useState(0);
  
  // Listen for cookie consent changes to trigger re-render
  useEffect(() => {
    const handleCookieConsentChange = () => {
      setCookieStatusChanged(prev => prev + 1);
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('cookieConsentChanged', handleCookieConsentChange);
      return () => {
        window.removeEventListener('cookieConsentChanged', handleCookieConsentChange);
      };
    }
  }, []);
  
  const shouldClip = element?.geometry?.hide_beyond_page_edges;

const finalWidth = shouldClip 
  ? maskedDimensions?.intersectingWidth 
  : resizeDimensions[element.id]?.width ?? dimensions?.width;

const finalHeight = shouldClip 
  ? maskedDimensions?.intersectingHeight 
  : resizeDimensions[element.id]?.height ?? dimensions?.height;

  // Calculate offset when element is outside PDF boundary (rounded for pixel-perfect alignment)
  const currentLeft = resizeDimensions[element.id]?.left ?? dimensions?.left;
  const currentTop = resizeDimensions[element.id]?.top ?? dimensions?.top;
  const offsetLeft = currentLeft < 0 ? Math.round(currentLeft) : 0;
  const offsetTop = currentTop < 0 ? Math.round(currentTop) : 0;

  switch (element?.element_type_name) {
    case "Image":
      return (
        element?.geometry?.hide_beyond_page_edges ?
          <div
            onClick={(event) => {
              elemClick(element?.id, event, element)
              handleClick(element)
              element?.dropdown_data && handleAction(element)
            }}
            style={{
               position:'absolute',
              overflow: 'hidden',
              width: `${maskedDimensions?.intersectingWidth }px`,
              height: `${maskedDimensions?.intersectingHeight }px`,
              left: `${offsetLeft < 0 ? -offsetLeft : 0}px`,
              top: `${offsetTop < 0 ? -offsetTop : 0}px`,
              background: `${bgColor}`,
            }}>
            <Image
              id={'image' + element?.id}
              src={element?.file_url ?? element?.value?.gif_link}
              alt="Uploaded image"
              onClick={(event) => { elemClick(element?.id, event, element)
              handleClick(element)}}
              onDragStart={(e) => e.preventDefault()}
              style={{
                position:'relative',
                height: `${resizeDimensions[element.id]?.height  || dimensions?.height }px`,
                width: `${resizeDimensions[element.id]?.width  || dimensions?.width }px`,
                left: `${offsetLeft}px`,
                top: `${offsetTop}px`,
                ...(element?.file_url && { objectFit: "contain" }),
                opacity: `${element?.transparency ?? 10}`,
                transform: `scaleX(${element?.flip ?? 1})`,
              }}
            />
          </div>
          :
          <Image
            id={'image' + element?.id}
            src={element?.file_url ?? element?.value?.gif_link}
            alt="Uploaded image"
            onDragStart={(e) => e.preventDefault()}
            style={{
              height: `${resizeDimensions[element.id]?.height || dimensions?.height }px`,
              width: `${resizeDimensions[element.id]?.width|| dimensions?.width }px`,
              ...(element?.file_url && { objectFit: "contain" }),
              transform: `scaleX(${element?.flip ?? 1})`,
              opacity: `${element?.transparency ?? 10}`,
            }}
            onClick={(event) => {
              elemClick(element?.id, event, element)
              handleClick(element)
              element?.dropdown_data && handleAction(element)
            }}
          />
      );
    case "Video":
      // Check if embedded content should be shown based on cookie consent
      // Cookie blocking only applies in viewer mode (LaserLink flow)
      const canShowVideo = shouldShowEmbeddedContent();
      
      if (!canShowVideo) {
        return (
          <CookieBlockedMessage
            style={{
              height: `${resizeDimensions[element.id]?.height || dimensions?.height}px`,
              width: `${resizeDimensions[element.id]?.width || dimensions?.width}px`,
            }}
            dimensions={dimensions}
          />
        );
      }
      
      if (element.value.source === 'vimeo') {
        return (
          <VimeoIframe
            onClick={(event) => { elemClick(element?.id, event, element)
              handleClick(element)}}
            setVideoData={setVideoData}
            actionCableVideoMontior={actionCableVideoMontior}
            style={{
              height: `${resizeDimensions[element.id]?.height  || dimensions?.height}px`,
              width: `${resizeDimensions[element.id]?.width  || dimensions?.width }px`,
            }}
            src={element?.value?.video_link}
            title="url"
            id={element?.id}
          />
        );
      } else if (element.value.source === 'youtube') {
        return (
          <YoutubeIframe
            onClick={(event) => { elemClick(element?.id, event, element)
              handleClick(element)}}
            actionCableVideoMontior={actionCableVideoMontior}
            setVideoData={setVideoData}
            style={{
              height: `${resizeDimensions[element.id]?.height - 3 || dimensions?.height - 3}px`,
              width: `${resizeDimensions[element.id]?.width - 3 || dimensions?.width - 3}px`,
            }}
            src={element?.value?.video_link}
            title="url"
            id={element?.id}
          />
        );
      }
      // If video source is not recognized, return null (shouldn't happen in normal flow)
      return null;
    case "Web URL":
      // Check if embedded content should be shown based on cookie consent
      // Cookie blocking only applies in viewer mode (LaserLink flow)
      const canShowWebURL = shouldShowEmbeddedContent();

      if (!canShowWebURL) {
        return (
          <CookieBlockedMessage
            style={{
              height: `${resizeDimensions[element.id]?.height - 3 || dimensions?.height - 3}px`,
              width: `${resizeDimensions[element.id]?.width - 3 || dimensions?.width - 3}px`,
            }}
            dimensions={dimensions}
          />
        );
      }

      // SIMPLE FIX: If video_link contains <script> tag, inject HTML directly (ThriveCart, etc.)
      if (/<script/i.test(element?.value?.video_link)) {
        return (
          <ScriptEmbed
            htmlContent={element.value.video_link}
            height={resizeDimensions[element.id]?.height - 3 || dimensions?.height - 3}
            width={resizeDimensions[element.id]?.width - 3 || dimensions?.width - 3}
            onClick={(event) => {
              elemClick(element?.id, event, element);
              handleClick(element);
            }}
          />
        );
      }

      // Standard iframe embed (YouTube, Vimeo, etc.)
      return (
        <SafeIFrame
          onClick={(event) => { elemClick(element?.id, event, element)
              handleClick(element)}}
          style={{
            height: `${resizeDimensions[element.id]?.height - 3 || dimensions?.height - 3}px`,
            width: `${resizeDimensions[element.id]?.width - 3 || dimensions?.width - 3}px`,
          }}
          scrolling={element?.value?.no_scroll ? 'no' : 'yes'}
          src={element?.value?.video_link}
          title="url"
          id={element?.id}
        />
      );
    case "HTML Text":
      return <HtmlText
        zoom={zoom}
        element={element}
        resizeDimensions={resizeDimensions}
        dimensions={dimensions}
        onClick={(event) => {
          elemClick(element?.id, event, element)
          handleClick(element)
          element?.dropdown_data && handleAction(element)
        }}
        elemClick={elemClick}
        handleClick={handleClick}
        handleAction={handleAction}
        htmlContent={element?.value?.html_text}
      />;
    case "Hotspot":
      const hotspotContent = (
        <div
          onClick={(event) => {
            elemClick(element?.id, event, element)
            handleClick(element)
            element?.dropdown_data && handleAction(element)
          }}
          style={{
            position: element?.geometry?.hide_beyond_page_edges ? 'relative' : 'static',
            height: `${resizeDimensions[element.id]?.height || dimensions?.height}px`,
            width: `${resizeDimensions[element.id]?.width || dimensions?.width}px`,
            left: element?.geometry?.hide_beyond_page_edges ? `${offsetLeft}px` : '0',
            top: element?.geometry?.hide_beyond_page_edges ? `${offsetTop}px` : '0',
            background: `${bgColor}`,
            cursor: 'pointer',
          }}
        />
      );
      
      return element?.geometry?.hide_beyond_page_edges ? (
        <div
          onClick={(event) => {
            elemClick(element?.id, event, element)
            handleClick(element)
            element?.dropdown_data && handleAction(element)
          }}
          style={{
            position: 'absolute',
            overflow: 'hidden',
            width: `${maskedDimensions?.intersectingWidth}px`,
            height: `${maskedDimensions?.intersectingHeight}px`,
            left: `${offsetLeft < 0 ? -offsetLeft : 0}px`,
            top: `${offsetTop < 0 ? -offsetTop : 0}px`,
          }}
        >
          {hotspotContent}
        </div>
      ) : hotspotContent;
    default:
      return <></>;
  }
};

export default Element;
